import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  getSupportTickets,
  createSupportTicket,
  updateTicketStatus,
  deleteSupportTicket,
  replyToSupportTicket,
  type SupportTicket,
} from "../../services/support";

// Backend ticket vocabulary — keep aligned with prisma enum / route validation.
type TicketStatus = "open" | "in_progress" | "resolved" | "closed";
type TicketPriority = "low" | "normal" | "high" | "urgent";

const STATUS_FILTERS: ReadonlyArray<TicketStatus | "all"> = [
  "all",
  "open",
  "in_progress",
  "resolved",
  "closed",
];

const PRIORITY_OPTIONS: TicketPriority[] = ["low", "normal", "high", "urgent"];

export default function Support() {
  const { t } = useTranslation();

  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<TicketStatus | "all">("all");

  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [ticketForm, setTicketForm] = useState<{ subject: string; message: string; priority: TicketPriority }>({
    subject: "",
    message: "",
    priority: "normal",
  });
  const [creating, setCreating] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  // Reply state for the details modal — kept local so the textarea
  // doesn't get blown away when the ticket list refreshes around it.
  const [replyText, setReplyText] = useState("");
  const [replyStatus, setReplyStatus] = useState<TicketStatus>("resolved");
  const [sendingReply, setSendingReply] = useState(false);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const data = await getSupportTickets();
      setTickets(data);
    } catch (err: any) {
      console.error("Failed to load support tickets:", err);
      toast.error(err?.response?.data?.error || "Nu s-au putut încărca tichetele de suport");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const handleCreateTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticketForm.subject.trim() || !ticketForm.message.trim()) {
      toast.error("Titlul și descrierea sunt obligatorii");
      return;
    }
    setCreating(true);
    try {
      const created = await createSupportTicket({
        subject: ticketForm.subject.trim(),
        message: ticketForm.message.trim(),
        priority: ticketForm.priority,
      });
      setTickets((prev) => [created, ...prev]);
      toast.success("Tichet creat cu succes");
      setShowCreateModal(false);
      setTicketForm({ subject: "", message: "", priority: "normal" });
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Nu s-a putut crea tichetul");
    } finally {
      setCreating(false);
    }
  };

  const handleStatusChange = async (ticket: SupportTicket, newStatus: TicketStatus) => {
    if (ticket.status === newStatus) return;
    setUpdatingId(ticket.id);
    try {
      const updated = await updateTicketStatus(ticket.id, newStatus);
      setTickets((prev) => prev.map((tk) => (tk.id === ticket.id ? updated : tk)));
      if (selectedTicket?.id === ticket.id) setSelectedTicket(updated);
      toast.success("Status actualizat");
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Nu s-a putut actualiza statusul");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleSendReply = async () => {
    if (!selectedTicket) return;
    if (!replyText.trim()) {
      toast.error("Scrie un răspuns înainte să trimiți");
      return;
    }
    setSendingReply(true);
    try {
      const updated = await replyToSupportTicket(selectedTicket.id, {
        response: replyText.trim(),
        status: replyStatus,
      });
      setTickets((prev) => prev.map((tk) => (tk.id === updated.id ? updated : tk)));
      setSelectedTicket(updated);
      setReplyText("");
      toast.success("Răspuns trimis. Solicitantul a primit email cu răspunsul.");
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Nu s-a putut trimite răspunsul");
    } finally {
      setSendingReply(false);
    }
  };

  const openDetailsFor = (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    setReplyText("");
    setReplyStatus(ticket.status === "open" ? "resolved" : (ticket.status as TicketStatus));
    setShowDetailsModal(true);
  };

  const handleDelete = async (ticket: SupportTicket) => {
    if (!window.confirm(`Ștergi tichetul "${ticket.subject}"?`)) return;
    try {
      await deleteSupportTicket(ticket.id);
      setTickets((prev) => prev.filter((tk) => tk.id !== ticket.id));
      if (selectedTicket?.id === ticket.id) {
        setShowDetailsModal(false);
        setSelectedTicket(null);
      }
      toast.success("Tichet șters");
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Nu s-a putut șterge tichetul");
    }
  };

  const filteredTickets =
    statusFilter === "all" ? tickets : tickets.filter((tk) => tk.status === statusFilter);

  const getStatusColor = (status: TicketStatus) => {
    switch (status) {
      case "open":
        return "bg-blue-50 text-blue-600 border-blue-200";
      case "in_progress":
        return "bg-amber-50 text-amber-600 border-amber-200";
      case "resolved":
        return "bg-emerald-50 text-emerald-600 border-emerald-200";
      case "closed":
        return "bg-slate-100 text-slate-500 border-slate-200";
    }
  };

  const getPriorityColor = (priority: TicketPriority) => {
    switch (priority) {
      case "low":
        return "bg-slate-100 text-slate-500";
      case "normal":
        return "bg-blue-50 text-blue-600";
      case "high":
        return "bg-amber-50 text-amber-600";
      case "urgent":
        return "bg-red-50 text-red-500";
    }
  };

  // dd/mm/yyyy hh:mm
  const formatDateTime = (iso: string) =>
    new Date(iso).toLocaleString("ro-RO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const stats = {
    open: tickets.filter((tk) => tk.status === "open").length,
    in_progress: tickets.filter((tk) => tk.status === "in_progress").length,
    resolved: tickets.filter((tk) => tk.status === "resolved").length,
    urgent: tickets.filter((tk) => tk.priority === "urgent").length,
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-rose-50 rounded-xl">
              <svg className="w-8 h-8 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Suport</h1>
              <p className="text-slate-500 mt-1">Tichete de suport ridicate de cluburi și utilizatori</p>
            </div>
          </div>
        </div>

        {/* Filters + Create */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex flex-wrap gap-2">
              {STATUS_FILTERS.map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                    statusFilter === status
                      ? "bg-rose-500 text-white"
                      : "bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200"
                  }`}
                >
                  {status === "all"
                    ? "Toate"
                    : status === "in_progress"
                    ? "În lucru"
                    : status === "open"
                    ? "Deschise"
                    : status === "resolved"
                    ? "Rezolvate"
                    : "Închise"}
                </button>
              ))}
            </div>

            <button
              onClick={() => setShowCreateModal(true)}
              className="px-5 py-2.5 bg-rose-500 hover:bg-rose-600 text-white rounded-xl font-medium transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Tichet nou
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Deschise", value: stats.open, color: "blue" },
            { label: "În lucru", value: stats.in_progress, color: "amber" },
            { label: "Rezolvate", value: stats.resolved, color: "emerald" },
            { label: "Urgente", value: stats.urgent, color: "red" },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
              <p className="text-sm text-slate-500">{s.label}</p>
              <p className={`text-2xl font-bold text-${s.color}-600`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Tickets list */}
        {loading ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
            <div className="inline-flex items-center gap-3">
              <div className="w-6 h-6 border-2 border-rose-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-slate-600">Se încarcă...</span>
            </div>
          </div>
        ) : filteredTickets.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
            <div className="p-4 bg-slate-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
              </svg>
            </div>
            <p className="text-slate-500">
              {tickets.length === 0 ? "Niciun tichet de suport încă" : "Niciun tichet pentru filtrul curent"}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredTickets.map((ticket) => (
              <div
                key={ticket.id}
                className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 hover:border-slate-300 transition-all"
              >
                <div className="flex items-start justify-between mb-4 gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-slate-800 mb-2 break-words">{ticket.subject}</h3>
                    <p className="text-sm text-slate-500 mb-3 line-clamp-2 whitespace-pre-wrap">{ticket.message}</p>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
                      <span className="flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        {ticket.user?.full_name || ticket.user?.email || "—"}
                      </span>
                      {ticket.club?.name && (
                        <>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1" />
                            </svg>
                            {ticket.club.name}
                          </span>
                        </>
                      )}
                      <span>•</span>
                      <span>{formatDateTime(ticket.created_at)}</span>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    <select
                      value={ticket.status}
                      disabled={updatingId === ticket.id}
                      onChange={(e) => handleStatusChange(ticket, e.target.value as TicketStatus)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border cursor-pointer disabled:opacity-50 ${getStatusColor(
                        ticket.status as TicketStatus
                      )}`}
                    >
                      <option value="open">Deschis</option>
                      <option value="in_progress">În lucru</option>
                      <option value="resolved">Rezolvat</option>
                      <option value="closed">Închis</option>
                    </select>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority as TicketPriority)}`}>
                      {ticket.priority.toUpperCase()}
                    </span>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-100 flex gap-2">
                  <button
                    onClick={() => openDetailsFor(ticket)}
                    className="px-4 py-2 bg-rose-500 hover:bg-rose-600 text-white rounded-xl text-sm font-medium transition-colors"
                  >
                    {ticket.admin_response ? "Vezi detalii" : "Răspunde"}
                  </button>
                  <button
                    onClick={() => handleDelete(ticket)}
                    className="px-4 py-2 bg-slate-50 hover:bg-red-50 text-slate-600 hover:text-red-600 rounded-xl text-sm font-medium transition-colors border border-slate-200"
                  >
                    Șterge
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Details modal */}
        {showDetailsModal && selectedTicket && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-6 border-b border-slate-200 flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-bold text-slate-800">{selectedTicket.subject}</h2>
                  <p className="text-sm text-slate-500 mt-1">
                    De la {selectedTicket.user?.full_name || selectedTicket.user?.email}
                    {selectedTicket.club?.name ? ` · ${selectedTicket.club.name}` : ""}
                  </p>
                </div>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(selectedTicket.status as TicketStatus)}`}>
                    {selectedTicket.status === "in_progress" ? "ÎN LUCRU" : selectedTicket.status.toUpperCase()}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityColor(selectedTicket.priority as TicketPriority)}`}>
                    {selectedTicket.priority.toUpperCase()}
                  </span>
                </div>

                <div className="bg-slate-50 rounded-xl p-4 mb-4">
                  <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Mesaj solicitant</p>
                  <p className="text-slate-700 whitespace-pre-wrap">{selectedTicket.message}</p>
                </div>

                {selectedTicket.admin_response && (
                  <div className="bg-emerald-50 border-l-4 border-emerald-500 rounded-xl p-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">
                        Răspunsul echipei
                      </p>
                      {selectedTicket.admin_response_at && (
                        <span className="text-xs text-emerald-700">
                          {formatDateTime(selectedTicket.admin_response_at)}
                        </span>
                      )}
                    </div>
                    {selectedTicket.responded_by && (
                      <p className="text-xs text-emerald-700 mb-2">
                        de {selectedTicket.responded_by.full_name || selectedTicket.responded_by.email}
                      </p>
                    )}
                    <p className="text-slate-700 whitespace-pre-wrap">{selectedTicket.admin_response}</p>
                  </div>
                )}

                <div className="text-sm text-slate-500 space-y-1 mb-6">
                  <p>Creat: {formatDateTime(selectedTicket.created_at)}</p>
                  <p>Actualizat: {formatDateTime(selectedTicket.updated_at)}</p>
                  {selectedTicket.resolved_at && (
                    <p>Rezolvat: {formatDateTime(selectedTicket.resolved_at)}</p>
                  )}
                </div>

                {/* Reply composer — sends mail to the original reporter */}
                <div className="border-t border-slate-200 pt-6">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l9 6 9-6M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    {selectedTicket.admin_response ? "Trimite un nou răspuns" : "Trimite răspuns"}
                  </h3>
                  <p className="text-xs text-slate-500 mb-3">
                    Răspunsul va fi trimis pe email către <span className="font-medium">{selectedTicket.user?.email}</span> și salvat pe tichet.
                  </p>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    rows={5}
                    placeholder="Scrie răspunsul aici..."
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 resize-none"
                  />
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">
                        Status după trimitere
                      </label>
                      <select
                        value={replyStatus}
                        onChange={(e) => setReplyStatus(e.target.value as TicketStatus)}
                        className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                      >
                        <option value="open">Deschis</option>
                        <option value="in_progress">În lucru</option>
                        <option value="resolved">Rezolvat</option>
                        <option value="closed">Închis</option>
                      </select>
                    </div>
                    <div className="flex items-end">
                      <button
                        onClick={handleSendReply}
                        disabled={sendingReply || !replyText.trim()}
                        className="w-full px-4 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {sendingReply ? "Se trimite..." : "Trimite răspuns"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-xl">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-slate-800">Tichet nou</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <form onSubmit={handleCreateTicket} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Titlu <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={ticketForm.subject}
                    onChange={(e) => setTicketForm({ ...ticketForm, subject: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                    placeholder="Descriere scurtă a problemei"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Mesaj <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    required
                    rows={6}
                    value={ticketForm.message}
                    onChange={(e) => setTicketForm({ ...ticketForm, message: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Prioritate</label>
                  <select
                    value={ticketForm.priority}
                    onChange={(e) => setTicketForm({ ...ticketForm, priority: e.target.value as TicketPriority })}
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                  >
                    {PRIORITY_OPTIONS.map((p) => (
                      <option key={p} value={p}>
                        {p === "normal" ? "Normală" : p === "low" ? "Mică" : p === "high" ? "Mare" : "Urgentă"}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 px-4 py-3 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition-colors"
                  >
                    Anulează
                  </button>
                  <button
                    type="submit"
                    disabled={creating}
                    className="flex-1 px-4 py-3 bg-rose-500 hover:bg-rose-600 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
                  >
                    {creating ? "Se creează..." : "Creează tichet"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
